import React, { useState } from "react";
import { Flag, Send, X } from "lucide-react";
import { toast } from "sonner";
import { reportQuestion, trackEvent } from "@/lib/api";
import { STUDY } from "@/constants/testIds";

const REASONS = [
  { value: "unclear", label: "The wording is unclear" },
  { value: "possible_answer_issue", label: "The answer may be incorrect" },
  { value: "outdated_source", label: "The source may be outdated" },
  { value: "typo", label: "There is a typo" },
  { value: "other", label: "Something else" },
];

export default function QuestionReportButton({ questionId }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("unclear");
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await reportQuestion(questionId, reason, details);
      trackEvent("question_reported", { question_id: questionId, reason });
      setSent(true);
      setOpen(false);
      toast.success("Question report sent", {
        description: "Thank you. We will review the question and its source.",
      });
    } catch (error) {
      toast.error("Could not send the report", {
        description: error?.response?.data?.detail || "Please try again.",
      });
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-[#15803d]">
        <Flag className="h-3.5 w-3.5" /> Report received
      </span>
    );
  }

  return (
    <div className="mt-4 border-t border-[color:rgba(10,27,51,0.10)] pt-4">
      {!open ? (
        <button
          type="button"
          data-testid={STUDY.questionReport}
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[color:rgba(11,18,32,0.58)] hover:text-[var(--ibp-navy-950)]"
        >
          <Flag className="h-3.5 w-3.5" /> Report this question
        </button>
      ) : (
        <div>
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-semibold text-[var(--ibp-ink)]">What should we review?</p>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close question report"
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-[color:rgba(11,18,32,0.55)] hover:bg-[color:rgba(10,27,51,0.06)]"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
            <select
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              className="h-11 rounded-xl border border-[color:rgba(10,27,51,0.18)] bg-white px-3 text-sm text-[var(--ibp-ink)] outline-none focus:border-[var(--ibp-navy-950)]"
            >
              {REASONS.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
            <textarea
              value={details}
              onChange={(event) => setDetails(event.target.value)}
              rows={2}
              maxLength={1200}
              placeholder="Optional details"
              className="min-h-11 resize-y rounded-xl border border-[color:rgba(10,27,51,0.18)] bg-white px-3 py-2 text-sm text-[var(--ibp-ink)] outline-none focus:border-[var(--ibp-navy-950)]"
            />
          </div>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            className="mt-3 inline-flex min-h-10 items-center gap-2 rounded-xl bg-[var(--ibp-navy-950)] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
          >
            <Send className="h-4 w-4" /> {busy ? "Sending..." : "Send report"}
          </button>
        </div>
      )}
    </div>
  );
}
